import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { formatCurrency } from '../../lib/utils';
import {
  FileText,
  Users,
  Clock,
  CurrencyDollar,
  CheckCircle,
  XCircle,
  HourglassSimple,
} from '@phosphor-icons/react';

const StatCard = ({ icon: Icon, label, value, subValue, color, iconBg, onClick, testid }) => (
  <div
    className={`ledger-cell ${
      onClick ? 'cursor-pointer hover:bg-slate-50 transition-colors' : ''
    }`}
    onClick={onClick}
    data-testid={testid}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
        <p
          className="mt-1 text-2xl font-bold text-slate-900"
          style={{ fontFamily: 'Manrope' }}
        >
          {value}
        </p>
        {subValue && <p className="text-sm text-slate-500 mt-0.5">{subValue}</p>}
      </div>
      <div className={`p-2.5 rounded-lg ${iconBg || 'bg-slate-100'}`}>
        <Icon size={22} className={color || 'text-slate-600'} weight="duotone" />
      </div>
    </div>
  </div>
);

export const PrimaryStatsGrid = ({ stats, onCardClick }) => (
  <div className="ledger-grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 rounded-xl overflow-hidden">
    <StatCard
      icon={FileText}
      label="Total Claims"
      value={stats?.total_claims || 0}
      subValue={`${stats?.monthly_claims || 0} this month`}
      iconBg="bg-indigo-50"
      color="text-indigo-600"
      onClick={() => onCardClick('all', 'all')}
      testid="stat-card-total-claims"
    />
    <StatCard
      icon={HourglassSimple}
      label="Pending"
      value={stats?.pending_claims || 0}
      subValue="Awaiting submission"
      iconBg="bg-amber-50"
      color="text-amber-600"
      onClick={() => onCardClick('status', 'Pending')}
      testid="stat-card-pending"
    />
    <StatCard
      icon={CheckCircle}
      label="Paid"
      value={stats?.paid_claims || 0}
      subValue="Claims paid"
      iconBg="bg-emerald-50"
      color="text-emerald-600"
      onClick={() => onCardClick('status', 'Paid')}
      testid="stat-card-paid"
    />
    <StatCard
      icon={CurrencyDollar}
      label="Total Revenue"
      value={formatCurrency(stats?.total_reimbursement || 0)}
      subValue={`${formatCurrency(stats?.monthly_reimbursement || 0)} this month`}
      iconBg="bg-blue-50"
      color="text-blue-600"
      onClick={() => onCardClick('status', 'Paid')}
      testid="stat-card-total-revenue"
    />
  </div>
);

const SecondaryCard = ({ icon: Icon, label, value, color, iconBg, hoverColor, isActive, onClick, testid }) => (
  <Card
    className={`border-slate-200 cursor-pointer ${hoverColor} transition-colors ${
      isActive ? 'ring-2 ring-indigo-200' : ''
    }`}
    onClick={onClick}
    data-testid={testid}
  >
    <CardContent className="pt-6">
      <div className="flex items-center gap-4">
        <div className={`p-3 ${iconBg} rounded-xl`}>
          <Icon size={24} className={color} weight="duotone" />
        </div>
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const SecondaryStatsGrid = ({ stats, activeFilter, onCardClick, onPatientsClick }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
    <SecondaryCard
      icon={Users}
      label="Active Patients"
      value={stats?.total_patients || 0}
      iconBg="bg-indigo-50"
      color="text-indigo-600"
      hoverColor="hover:border-indigo-300"
      onClick={onPatientsClick}
      testid="stat-card-active-patients"
    />
    <SecondaryCard
      icon={XCircle}
      label="Rejected Claims"
      value={stats?.rejected_claims || 0}
      iconBg="bg-red-50"
      color="text-red-600"
      hoverColor="hover:border-red-300"
      isActive={activeFilter?.value === 'Rejected'}
      onClick={() => onCardClick('status', 'Rejected')}
      testid="stat-card-rejected-claims"
    />
    <SecondaryCard
      icon={Clock}
      label="Submitted"
      value={stats?.submitted_claims || 0}
      iconBg="bg-blue-50"
      color="text-blue-600"
      hoverColor="hover:border-blue-300"
      isActive={activeFilter?.value === 'Submitted'}
      onClick={() => onCardClick('status', 'Submitted')}
      testid="stat-card-submitted-claims"
    />
  </div>
);
